import React, { useState, useEffect } from 'react';
import { X, RefreshCw, CheckCircle, Loader } from 'lucide-react';
import { Button } from '../../common';
import tenantsService from '../../../services/api/tenants.service';

const QRCodeModal = ({ instance, onClose, onConnected }) => {
  const [qrCode, setQrCode] = useState(instance.qrcode);
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [checking, setChecking] = useState(false);

  useEffect(() => {
    // Verificar status inicial
    checkStatus();

    // Polling para verificar conexão (a cada 3 segundos)
    const interval = setInterval(checkStatus, 3000);

    return () => clearInterval(interval);
  }, [instance.name]);

  const checkStatus = async () => {
    try {
      setChecking(true);
      const data = await tenantsService.getEvolutionInstanceStatus(instance.name);
      const instanceStatus = data.status?.instance?.state || data.instanceName?.state;
      setStatus(instanceStatus);

      // Se conectou, notificar e fechar após 2 segundos
      if (instanceStatus === 'open') {
        setTimeout(() => {
          onConnected && onConnected();
          onClose();
        }, 2000);
      }
    } catch (error) {
      console.error('Erro ao verificar status:', error);
    } finally {
      setChecking(false);
    }
  };

  const refreshQRCode = async () => {
    try {
      setLoading(true);
      const data = await tenantsService.getEvolutionQRCode(instance.name);
      setQrCode(data.qrcode);
    } catch (error) {
      alert('Erro ao atualizar QR Code: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full animate-scale-in">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">QR Code WhatsApp</h2>
            <p className="text-sm text-gray-600 mt-1">
              Instância: {instance.name}
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* Body */}
        <div className="p-6">
          {/* Status */}
          <div className="mb-6">
            {status === 'open' ? (
              <div className="flex items-center gap-3 p-4 bg-green-50 border border-green-200 rounded-lg">
                <CheckCircle className="w-6 h-6 text-green-500" />
                <div>
                  <p className="font-semibold text-green-900">WhatsApp Conectado!</p>
                  <p className="text-sm text-green-700">A instância está pronta para uso.</p>
                </div>
              </div>
            ) : status === 'connecting' ? (
              <div className="flex items-center gap-3 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                <Loader className="w-6 h-6 text-yellow-500 animate-spin" />
                <div>
                  <p className="font-semibold text-yellow-900">Conectando...</p>
                  <p className="text-sm text-yellow-700">Aguardando conexão com WhatsApp.</p>
                </div>
              </div>
            ) : (
              <div className="flex items-center gap-3 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <div className="flex-shrink-0">
                  {checking ? (
                    <Loader className="w-6 h-6 text-blue-500 animate-spin" />
                  ) : (
                    <div className="w-6 h-6 bg-blue-500 rounded-full flex items-center justify-center">
                      <div className="w-3 h-3 bg-white rounded-full"></div>
                    </div>
                  )}
                </div>
                <div>
                  <p className="font-semibold text-blue-900">Aguardando QR Code</p>
                  <p className="text-sm text-blue-700">Escaneie o código abaixo com seu WhatsApp.</p>
                </div>
              </div>
            )}
          </div>

          {/* QR Code */}
          {qrCode && status !== 'open' && (
            <div className="space-y-4">
              <div className="flex justify-center p-6 bg-gray-50 rounded-lg">
                {qrCode.base64 ? (
                  <img 
                    src={qrCode.base64} 
                    alt="QR Code WhatsApp" 
                    className="w-64 h-64 border-4 border-white rounded-lg shadow-lg"
                  />
                ) : (
                  <div className="w-64 h-64 bg-gray-200 rounded-lg flex items-center justify-center">
                    <Loader className="w-12 h-12 text-gray-400 animate-spin" />
                  </div>
                )}
              </div>

              {/* Instruções */}
              <div className="space-y-2 text-sm text-gray-600">
                <p className="font-semibold text-gray-900">Como conectar:</p>
                <ol className="list-decimal list-inside space-y-1 ml-2">
                  <li>Abra o WhatsApp no seu celular</li>
                  <li>Vá em <strong>Configurações</strong> → <strong>Aparelhos conectados</strong></li>
                  <li>Toque em <strong>Conectar um aparelho</strong></li>
                  <li>Aponte o celular para esta tela para escanear o código</li>
                </ol>
              </div>

              {/* Refresh Button */}
              <Button
                variant="outline"
                onClick={refreshQRCode}
                loading={loading}
                leftIcon={<RefreshCw className="w-4 h-4" />}
                className="w-full"
              >
                Atualizar QR Code
              </Button>
            </div>
          )}

          {/* Connected State */}
          {status === 'open' && (
            <div className="text-center py-8">
              <CheckCircle className="w-24 h-24 text-green-500 mx-auto mb-4" />
              <p className="text-lg font-semibold text-gray-900 mb-2">
                Conectado com sucesso!
              </p>
              <p className="text-sm text-gray-600">
                Esta janela será fechada automaticamente...
              </p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 p-6 border-t border-gray-200 bg-gray-50">
          <Button
            variant="outline"
            onClick={onClose}
          >
            Fechar
          </Button>
        </div>
      </div>
    </div>
  );
};

export default QRCodeModal;